import { useAtom } from "jotai";
import classNames from "classnames";
import { toast } from "react-hot-toast";
import axiosInstance from "helpers/api";
import { useCookies } from "react-cookie";
import Button from "components/base/Button";
import { dateFormatter, formatErrorForReact } from "helpers/utils";
import { loadStripe } from "@stripe/stripe-js";
import { Card, Col, Row, Badge, Alert } from "react-bootstrap";
import configService from "services/configService";
import { user_info } from "jotai/UserJotaiInstance";
import { Fragment, useEffect, useState } from "react";
import { internalPricingItems } from "data/landing/pricingData";
import ConfirmDeleteModal from "components/modules/crm/common-components/ConfirmDeleteModal";
import SubscriptionConfirmModal from "./SubscriptionConfirmModal";
import PricingItemInternal from "components/modules/landing/default/pricing/PricingItemInternal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBolt, 
  faCheck, 
  faCrown, 
  faPhone, 
  faStar, 
  faUsers, 
  faZap,
  faBuilding,
  faRobot,
  faGlobe 
} from "@fortawesome/free-solid-svg-icons";
import AgentUnderBrokerSubscription from "./AgentUnderBrokerSubscription";

const Subscription = () => {
  const [cookie, setCookie] = useCookies(["isLoggedInKIQ", "stripeIdpKey"]);

  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userSubscription, setUserSubscription] = useState<any>(null);
  const [selectedAddOns, setSelectedAddOns] = useState<{[key: string]: boolean}>({});
  const [processingAddOn, setProcessingAddOn] = useState<string | null>(null);
  
  // New state for brokerage detection
  const [isAgentUnderBroker, setIsAgentUnderBroker] = useState(false);
  const [brokerageInfo, setBrokerageInfo] = useState<any>(null);
  const [loadingBrokerageStatus, setLoadingBrokerageStatus] = useState(true);
  
  // Modal confirmation state
  const [pendingSubscription, setPendingSubscription] = useState<{
    type: string;
    interval: string;
    addOns: string[];
    isUpgrade: boolean;
    prorationData?: any;
  } | null>(null);

  const [, setUserInfo] = useAtom(user_info);

  // Initialize Stripe with secure configuration
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        const stripeConfig = await configService.getStripeConfig();
        const stripe = loadStripe(stripeConfig.publishableKey);
        setStripePromise(stripe);
        console.log('✅ [Stripe] Initialized with secure publishable key');
      } catch (error) {
        console.warn('⚠️ [Stripe] Failed to get secure config, using fallback');
        const fallbackKey = process.env.REACT_APP_STRIPE_PUB_KEY as string;
        if (fallbackKey) {
          const stripe = loadStripe(fallbackKey);
          setStripePromise(stripe);
        } else {
          console.error('❌ [Stripe] No publishable key available');
          toast.error('Payment system unavailable. Please contact support.');
        }
      }
    };

    initializeStripe();
  }, []);
  
  // Check brokerage status
  useEffect(() => {
    checkBrokerageStatus();
  }, []);
  
  const checkBrokerageStatus = async () => {
    try {
      setLoadingBrokerageStatus(true);
      const response = await axiosInstance.get('/users/brokerage-status');
      
      if (response.data.success && response.data.brokerage) {
        setIsAgentUnderBroker(true);
        setBrokerageInfo(response.data.brokerage);
        console.log('🏢 User is part of brokerage:', response.data.brokerage.name);
      }
    } catch (error) {
      console.log('👤 User is not part of a brokerage');
    } finally {
      setLoadingBrokerageStatus(false);
    }
  };
  
  // Helper function to get plan price
  const getPlanPrice = (planType: string): number => {
    const prices: {[key: string]: number} = {
      'Essential': 35,
      'Professional': 250,
      'Premium': 350,
      'BrokerPlan': 1000
    };
    return prices[planType] || 250;
  };

  // Handle modal confirmation
  const handleConfirmSubscription = async () => {
    if (!pendingSubscription) return;
    
    try {
      setLoading(true);
      setShowConfirmModal(false);
      
      const { type, interval, addOns, isUpgrade, prorationData } = pendingSubscription;
      
      if (isUpgrade) {
        // Use upgrade endpoint with proration
        console.log("🚀 Calling upgrade endpoint");
        const response = await axiosInstance.post("/users/upgrade_subscription", {
          new_plan_name: type,
          add_ons: addOns
        });
        
        if (response.data.success) {
          toast.success("Subscription upgraded successfully! Refreshing...");
          setTimeout(async () => {
            await refreshSubscriptionData();
            window.location.reload();
          }, 2000);
        }
      } else {
        // New subscription flow
        console.log("🆕 Creating new subscription");
        const response = await axiosInstance.post("/users/create_checkout_session", {
          subscription_type: type,
          subscription_interval: interval,
          add_ons: addOns || []
        });
        
        if (response.data.success && response.data.data) {
          const stripe = await stripePromise;
          const result = await stripe.redirectToCheckout({
            sessionId: response.data.data
          });
          
          if (result.error) {
            throw result.error;
          }
        }
      }
      
      setPendingSubscription(null);
    } catch (error: any) {
      toast.error(formatErrorForReact(error?.response?.data?.message || error?.response?.data || error));
      setShowConfirmModal(false);
    } finally {
      setLoading(false);
    }
  };

  const refreshSubscriptionData = async () => {
    try {
      const response = await axiosInstance.get("/users/subscription");
      setUserSubscription(response.data.data);
    } catch (error) {
      console.error("Failed to refresh subscription data:", error);
    }
  };

  const cancelSubscription = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(
        `/users/user_subscription/${userSubscription.stripe_subscription_id}`
      );

      if (response.data.statusCode === 200) {
        toast.success("Subscription cancelled successfully");
        setUserSubscription(response.data.data.subscriptionData);
        const userResponse = await axiosInstance.get("/users/me");
        setUserInfo(userResponse.data.data.authenticatedUser);
      }

      setShowDeleteModal(false);
    } catch (error: any) {
      toast.error(formatErrorForReact(error?.response?.data?.message || error?.response?.data || error));
    } finally {
      setLoading(false);
    }
  };

  const handleBuySubscription = async (type: string, interval: string, addOns?: string[]) => {
    try {
      setLoading(true);
      
      const hasValidSubscription = userSubscription?.subscription_status === "Active" && 
                                  userSubscription?.stripe_subscription_id;
      
      const isExistingCustomer = userSubscription?.subscription_status === "Active" || 
                                userSubscription?.subscription_status === "FreeTrial" ||
                                userSubscription?.subscription_status === "Cancelled";
      
      console.log("🔍 Subscription check:", {
        hasValidSubscription,
        isExistingCustomer,
        status: userSubscription?.subscription_status,
        stripeSubId: userSubscription?.stripe_subscription_id,
        currentPlan: userSubscription?.type,
        newPlan: type
      });
      
      if (hasValidSubscription) {
        try {
          const previewResponse = await axiosInstance.post("/users/preview_upgrade", {
            new_plan_name: type,
            add_ons: addOns || []
          });
          
          const { isNewSubscription, prorationCredit, newPlanCharge, totalCharge, message } = previewResponse.data.data;
          
          if (!isNewSubscription) {
            setPendingSubscription({
              type,
              interval,
              addOns: addOns || [],
              isUpgrade: true,
              prorationData: { prorationCredit, newPlanCharge, totalCharge }
            });
            setShowConfirmModal(true);
            setLoading(false);
            return;
          }
        } catch (upgradeError: any) {
          console.log("⚠️ Upgrade attempt failed, falling back to new subscription:", upgradeError.message);
        }
      }
      
      // New subscription flow
      setPendingSubscription({
        type,
        interval,
        addOns: addOns || [],
        isUpgrade: false,
        prorationData: null
      });
      setShowConfirmModal(true);
      setLoading(false);
      return;

    } catch (error: any) {
      toast.error(formatErrorForReact(error?.response?.data?.message || error?.response?.data || error));
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserSubscription();
  }, []);

  const getUserSubscription = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/users/subscription");
      setUserSubscription(response.data.data);
      
      if (response.data.data?.add_ons) {
        const addOns = response.data.data.add_ons.reduce((acc: any, addon: string) => {
          acc[addon] = true;
          return acc;
        }, {});
        setSelectedAddOns(addOns);
      }
    } catch (error: any) {
      console.error("Failed to load subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  // If still loading brokerage status, show loading state
  if (loadingBrokerageStatus) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Checking subscription status...</p>
      </div>
    );
  }

  // If user is part of a brokerage (either as owner or member), show agent upgrade options
  // This allows both brokers and agents to upgrade their AI usage
  if (isAgentUnderBroker) {
    return (
      <AgentUnderBrokerSubscription 
        userSubscription={userSubscription}
        brokerageInfo={brokerageInfo}
        onSubscriptionUpdate={getUserSubscription}
      />
    );
  }

  // Otherwise show regular agent subscription options
  const currentPlanName = userSubscription?.type || '';
  const subscriptionStatus = userSubscription?.subscription_status || '';

  return (
    <Fragment>
      {/* Regular subscription content for individual agents */}
      <Row className="g-3 mb-4">
        <Col className="col-auto">
          <h5 className="mb-0 py-3 px-3">My Subscriptions</h5>
        </Col>
      </Row>
      
      {/* Current plan display */}
      {userSubscription && (
        <Card className="mb-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={8}>
                <h6 className="mb-1">Current Plan: {currentPlanName}</h6>
                <p className="mb-0 text-muted">
                  Status: <Badge bg={subscriptionStatus === 'Active' ? 'success' : 'warning'}>
                    {subscriptionStatus}
                  </Badge>
                </p>
              </Col>
              <Col md={4} className="text-end">
                {subscriptionStatus === 'Active' && (
                  <Button 
                    variant="danger" 
                    size="sm"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    Cancel Subscription
                  </Button>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Pricing options */}
      <Row>
        {internalPricingItems.map((item: any) => (
          <Col lg={4} className="mb-4" key={item.id}>
            <PricingItemInternal
              pricing={item}
              selectedAddOns={selectedAddOns}
              handleBuySubscription={handleBuySubscription}
              currentPlan={currentPlanName}
              loading={loading}
            />
          </Col>
        ))}
      </Row>

      {/* Modals */}
      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        handleDelete={cancelSubscription}
        text="Cancel Subscription"
        subText="Are you sure you want to cancel your subscription? You'll lose access to premium features at the end of your current billing period."
      />

      <SubscriptionConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSubscription}
        loading={loading}
        isUpgrade={pendingSubscription?.isUpgrade || false}
        currentPlan={currentPlanName}
        newPlan={pendingSubscription?.type || ''}
        price={getPlanPrice(pendingSubscription?.type || 'Professional')}
        prorationCredit={pendingSubscription?.prorationData?.prorationCredit}
        newPlanCharge={pendingSubscription?.prorationData?.newPlanCharge}
        totalCharge={pendingSubscription?.prorationData?.totalCharge}
        isExistingCustomer={userSubscription?.subscription_status === 'Active' || userSubscription?.subscription_status === 'FreeTrial' || userSubscription?.subscription_status === 'Cancelled' || false}
      />
    </Fragment>
  );
};

export default Subscription;